/**
 * Hook: Blog-Benachrichtigungs-Einstellungen
 *
 * Lädt das Opt-Out-Flag beim Mount, bietet `setEnabled(boolean)` mit
 * Optimistic UI. Wird im Profil-Tab konsumiert.
 *
 * Optimistic Pattern:
 *   - User klickt Toggle → setState sofort umschalten
 *   - REST-PATCH im Hintergrund
 *   - Bei Fehler: zurückrollen + Error setzen
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchBlogNotificationPreferences,
  updateBlogNotificationPreferences,
  type BlogNotificationPreferences,
} from '../services/blog/blogNotificationPreferencesService';

interface UseBlogNotificationPreferencesResult {
  preferences: BlogNotificationPreferences | null;
  isLoading: boolean;
  error: Error | null;
  setEnabled: (next: boolean) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useBlogNotificationPreferences(): UseBlogNotificationPreferencesResult {
  const [preferences, setPreferences] = useState<BlogNotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const prefs = await fetchBlogNotificationPreferences();
      setPreferences(prefs);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Einstellung konnte nicht geladen werden'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setEnabled = useCallback(async (next: boolean): Promise<void> => {
    setError(null);
    const previous = preferences;

    if (previous) {
      setPreferences({ ...previous, enabled: next });
    }

    try {
      const result = await updateBlogNotificationPreferences(next);
      setPreferences(result);
    } catch (err) {
      if (previous) setPreferences(previous);
      setError(err instanceof Error ? err : new Error('Einstellung konnte nicht gespeichert werden'));
      throw err;
    }
  }, [preferences]);

  return { preferences, isLoading, error, setEnabled, refresh };
}
